import { useState } from "react";
import { motion } from "framer-motion";
import { staggerContainer } from "../utils/animations";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null,
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    } else if (formData.message.length > 500) {
      newErrors.message = "Message must be less than 500 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormStatus({ isSubmitting: true, isSubmitted: false, error: null });

    try {
      // Using Web3Forms - a simple form service
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "9332fae2-d035-400b-8373-95b4285185f8", // Your Web3Forms access key
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          to: "sakib08.dev@gmail.com",
        }),
      });

      const result = await response.json();

      if (result.success) {
        console.log("Email sent successfully:", result);
        setFormStatus({ isSubmitting: false, isSubmitted: true, error: null });

        // Reset form after successful submission
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setFormStatus({
            isSubmitting: false,
            isSubmitted: false,
            error: null,
          });
        }, 5000);
      } else {
        throw new Error(result.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Email sending failed:", error);
      setFormStatus({
        isSubmitting: false,
        isSubmitted: false,
        error:
          "Failed to send message. Please try again or contact me directly at sakib08.dev@gmail.com",
      });
    }
  };

  return (
    <motion.section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-32 relative"
      id="contact"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, amount: 0.3 }}
      variants={staggerContainer}
    >
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-[100px] -z-10 pointer-events-none"></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
            Let's work <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              together.
            </span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-lg">
            I'm currently available for freelance work and open to new
            opportunities. If you have a project that needs some creative touch,
            or just want to say hi, feel free to drop a message.
          </p>
          <div className="space-y-6">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-primary border border-white/10 shadow-lg shrink-0">
                <span className="material-symbols-outlined text-xl">mail</span>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium mb-0.5 uppercase tracking-wider">
                  Email
                </p>
                <a
                  className="text-lg font-display font-bold text-white hover:text-primary transition-colors"
                  href="mailto:sakib08.dev@gmail.com"
                >
                  sakib08.dev@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-accent border border-white/10 shadow-lg shrink-0">
                <span className="material-symbols-outlined text-xl">call</span>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium mb-0.5 uppercase tracking-wider">
                  Phone
                </p>
                <a
                  className="text-lg font-display font-bold text-white hover:text-accent transition-colors"
                  href="tel:+8801303330278"
                >
                  +8801303330278
                </a>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-green-500 border border-white/10 shadow-lg shrink-0">
                <span className="material-symbols-outlined text-xl">chat</span>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium mb-0.5 uppercase tracking-wider">
                  WhatsApp
                </p>
                <a
                  className="text-lg font-display font-bold text-white hover:text-green-500 transition-colors"
                  href="https://wa.me/8801303330278"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  +8801303330278
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-card-bg/50 backdrop-blur-sm border border-white/10 rounded-[2.5rem] p-8 sm:p-10 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 rounded-full blur-[80px] -z-10 transition-all duration-500 group-hover:bg-accent/10"></div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Success Message */}
            {formStatus.isSubmitted && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
                <span className="material-symbols-outlined text-green-500">
                  check_circle
                </span>
                <p className="text-green-400 font-medium">
                  Message sent successfully! I'll get back to you soon.
                </p>
              </div>
            )}

            {/* Error Message */}
            {formStatus.error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
                <span className="material-symbols-outlined text-red-500">
                  error
                </span>
                <p className="text-red-400 font-medium">{formStatus.error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label
                  className="text-sm font-medium text-gray-400 ml-1"
                  htmlFor="name"
                >
                  Name *
                </label>
                <input
                  className={`w-full bg-black/20 border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all hover:border-white/20 ${
                    errors.name
                      ? "border-red-500/50 focus:ring-red-500/50"
                      : "border-white/10"
                  }`}
                  id="name"
                  name="name"
                  placeholder="John Doe"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                {errors.name && (
                  <p className="text-red-400 text-xs ml-1 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[12px]">
                      error
                    </span>
                    {errors.name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label
                  className="text-sm font-medium text-gray-400 ml-1"
                  htmlFor="email"
                >
                  Email *
                </label>
                <input
                  className={`w-full bg-black/20 border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all hover:border-white/20 ${
                    errors.email
                      ? "border-red-500/50 focus:ring-red-500/50"
                      : "border-white/10"
                  }`}
                  id="email"
                  name="email"
                  placeholder="john@example.com"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                {errors.email && (
                  <p className="text-red-400 text-xs ml-1 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[12px]">
                      error
                    </span>
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label
                className="text-sm font-medium text-gray-400 ml-1"
                htmlFor="subject"
              >
                Subject *
              </label>
              <input
                className={`w-full bg-black/20 border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all hover:border-white/20 ${
                  errors.subject
                    ? "border-red-500/50 focus:ring-red-500/50"
                    : "border-white/10"
                }`}
                id="subject"
                name="subject"
                placeholder="Project Inquiry"
                type="text"
                value={formData.subject}
                onChange={handleInputChange}
                required
              />
              {errors.subject && (
                <p className="text-red-400 text-xs ml-1 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[12px]">
                    error
                  </span>
                  {errors.subject}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label
                className="text-sm font-medium text-gray-400 ml-1"
                htmlFor="message"
              >
                Message *
              </label>
              <textarea
                className={`w-full bg-black/20 border rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all resize-none hover:border-white/20 ${
                  errors.message
                    ? "border-red-500/50 focus:ring-red-500/50"
                    : "border-white/10"
                }`}
                id="message"
                name="message"
                placeholder="Tell me about your project..."
                rows="4"
                value={formData.message}
                onChange={handleInputChange}
                required
              ></textarea>
              {errors.message && (
                <p className="text-red-400 text-xs ml-1 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[12px]">
                    error
                  </span>
                  {errors.message}
                </p>
              )}
              <p className="text-gray-500 text-xs ml-1">
                {formData.message.length}/500 characters
              </p>
            </div>
            <button
              className={`w-full font-bold text-lg py-4 rounded-xl shadow-lg transition-all transform flex items-center justify-center gap-2 mt-2 ${
                formStatus.isSubmitting
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-primary/25 active:scale-[0.98] group-hover:shadow-primary/40"
              }`}
              type="submit"
              disabled={formStatus.isSubmitting}
            >
              {formStatus.isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>Send Message</span>
                  <span className="material-symbols-outlined text-[20px] group-hover:translate-x-1 transition-transform">
                    send
                  </span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </motion.section>
  );
};

export default ContactSection;
